/**
 * Sliding idle session monitor.
 * Resets lastActivity on UI interactions; logs out via auth:expired after idle window.
 * Proactively refreshes JWT near expiry (throttled) so active users are not cut off.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { safeGet, safeSet } from './safeStorage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import {
  touchLastActivity,
  getLastActivityMs,
  getIdleTimeoutMs,
  getRefreshThresholdMs,
  getTokenExpirationMs,
  setAuthTokenCookie,
  emitAuthExpired,
  REFRESH_THROTTLE_MS,
} from './sessionIdle';
import { isTokenExpired } from './auth';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8010';
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;

let refreshInFlight = null;

async function refreshAuthTokenIfNeeded() {
  const token = safeGet(STORAGE_KEYS.authToken);
  if (!token || isTokenExpired(token)) return null;

  const expiration = getTokenExpirationMs(token);
  if (!expiration) return null;

  const timeUntilExpiration = expiration - Date.now();
  const threshold = getRefreshThresholdMs(token);
  if (timeUntilExpiration <= 0 || timeUntilExpiration >= threshold) return null;

  const lastRefresh = Number(safeGet(STORAGE_KEYS.lastTokenRefresh) || 0);
  if (lastRefresh && Date.now() - lastRefresh < REFRESH_THROTTLE_MS) return null;

  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await axios.post(
        `${API_BASE}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.access_token) {
        const newToken = response.data.access_token;
        safeSet(STORAGE_KEYS.authToken, newToken);
        safeSet(STORAGE_KEYS.lastTokenRefresh, String(Date.now()));
        setAuthTokenCookie(newToken);
        if (response.data.user) {
          safeSet(STORAGE_KEYS.user, JSON.stringify(response.data.user));
        }
        return newToken;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (isTokenExpired(token)) {
        emitAuthExpired({ source: 'refresh', expired: true });
      }
    } finally {
      refreshInFlight = null;
    }
    return null;
  })();

  return refreshInFlight;
}

function checkIdleExpiry() {
  const token = safeGet(STORAGE_KEYS.authToken);
  if (!token) return;

  if (isTokenExpired(token)) {
    emitAuthExpired({ source: 'jwt-exp' });
    return;
  }

  let last = getLastActivityMs();
  if (!last) {
    touchLastActivity(true);
    last = Date.now();
  }

  const idleLimit = getIdleTimeoutMs(token);
  if (Date.now() - last > idleLimit) {
    emitAuthExpired({ source: 'idle-timeout' });
  }
}

/**
 * Call once from Layout (authenticated shell).
 */
export function useSessionIdleMonitor() {
  const location = useLocation();

  useEffect(() => {
    if (!safeGet(STORAGE_KEYS.authToken)) return;
    touchLastActivity();
    refreshAuthTokenIfNeeded();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!safeGet(STORAGE_KEYS.authToken)) return undefined;

    touchLastActivity(true);

    const onActivity = () => {
      touchLastActivity();
      refreshAuthTokenIfNeeded();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        onActivity();
        checkIdleExpiry();
      }
    };

    const opts = { capture: true, passive: true };
    window.addEventListener('mousedown', onActivity, opts);
    window.addEventListener('keydown', onActivity, opts);
    window.addEventListener('touchstart', onActivity, opts);
    window.addEventListener('scroll', onActivity, opts);
    document.addEventListener('visibilitychange', onVisibility);

    const intervalId = setInterval(checkIdleExpiry, IDLE_CHECK_INTERVAL_MS);
    checkIdleExpiry();

    return () => {
      window.removeEventListener('mousedown', onActivity, opts);
      window.removeEventListener('keydown', onActivity, opts);
      window.removeEventListener('touchstart', onActivity, opts);
      window.removeEventListener('scroll', onActivity, opts);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(intervalId);
    };
  }, []);
}

export { refreshAuthTokenIfNeeded };
